import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CryptoJS from "crypto-js";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useSnackbar } from "../contexts/SnackbarContext";
import { updateInfoPassword } from "../services/user.service";
import CustomDialog from "./CustomDialog";

type PropsType = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

const ChangePasswordForm = (props: PropsType) => {
  const { showSnackbar } = useSnackbar();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickShowPassword = (inputType: string = "PASSWORD") => {
    if (inputType === "OLD_PASSWORD") {
      setShowOldPassword((show) => !show);
    } else if (inputType === "CONFIRM_PASSWORD") {
      setShowConfirmPassword((show) => !show);
    } else {
      setShowPassword((show) => !show);
    }
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const formSchema = z
    .object({
      old_password: z.string().min(1, {
        message: "Old password is required. Please enter a old password.",
      }),
      password: z
        .string()
        .min(8, {
          message: "Password must be at least 8 characters long.",
        })
        .regex(/[a-zA-Z]/, {
          message: "Password must contain both letters and numbers.",
        })
        .regex(/\d/, {
          message: "Password must contain both letters and numbers.",
        }),
      confirm_password: z.string().min(1, {
        message:
          "Confirm password is required. Please enter a confirm password.",
      }),
    })
    .refine((data) => data.password === data.confirm_password, {
      message: "Passwords don't match.",
      path: ["confirm_password"], // Specify which field the error is related to
    })
    .refine(
      (data) => {
        if (data.password && !data.old_password) {
          return false;
        }
        return true;
      },
      {
        message: "Old password is required when setting a new password.",
        path: ["old_password"],
      },
    );

  const { control, handleSubmit, watch } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      old_password: "",
      password: "",
      confirm_password: "",
    },
  });

  const password = watch("password", "");

  const isPasswordLongEnough = password && password.length >= 8;
  const isPasswordContainsLettersAndNumbers =
    password && /[a-zA-Z]/.test(password) && /\d/.test(password);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    let infoPassword = {
      old_password: "",
      new_password: "",
    };

    if (values.old_password && values?.password) {
      infoPassword.old_password = CryptoJS.SHA256(
        values.old_password,
      ).toString();
      infoPassword.new_password = CryptoJS.SHA256(values.password).toString();
    }

    setIsLoading(true);
    try {
      if (infoPassword?.old_password && infoPassword?.new_password) {
        await updateInfoPassword(infoPassword);
      }

      props.setOpen(false);
      showSnackbar("Changes Saved Successfully!", "success");
    } catch (error) {
      console.error(error);
      showSnackbar("Changes Saved Failed", "error");
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <CustomDialog
      open={props.open}
      onClose={() => props.setOpen(false)}
      title={"Change Password"}
      content={
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box>
            <Stack useFlexGap spacing={"12px"} mb={4}>
              <InputLabel htmlFor="old_password" required={password !== ""}>
                Old Password
              </InputLabel>
              <Controller
                name="old_password"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      placeholder="Old Password"
                      fullWidth
                      inputRef={ref}
                      type={showOldPassword ? "text" : "password"}
                      error={!!error}
                      helperText={error?.message}
                      onChange={onChange}
                      value={value || ""}
                      autoComplete="off"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showOldPassword
                                    ? "hide the password"
                                    : "display the password"
                                }
                                onClick={() =>
                                  handleClickShowPassword("OLD_PASSWORD")
                                }
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                              >
                                {showOldPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </>
                )}
              />

              <InputLabel htmlFor="password">Password</InputLabel>
              <Controller
                name="password"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <>
                    <TextField
                      placeholder="Password"
                      fullWidth
                      inputRef={ref}
                      type={showPassword ? "text" : "password"}
                      error={!!error}
                      onChange={onChange}
                      value={value}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? "hide the password"
                                    : "display the password"
                                }
                                onClick={() => handleClickShowPassword()}
                                onMouseDown={handleMouseDownPassword}
                                onMouseUp={handleMouseUpPassword}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                    <div>
                      <Typography
                        sx={{ paddingLeft: "16px", fontSize: "0.75rem" }}
                        color={
                          isPasswordLongEnough ? "success.main" : "error.main"
                        }
                      >
                        Password must be at least 8 characters long.
                      </Typography>
                      <Typography
                        sx={{ paddingLeft: "16px", fontSize: "0.75rem" }}
                        color={
                          isPasswordContainsLettersAndNumbers
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        Must contain both letters and numbers.
                      </Typography>
                    </div>
                  </>
                )}
              />

              <InputLabel htmlFor="confirm_password" required>
                Confirm Password
              </InputLabel>
              <Controller
                name="confirm_password"
                control={control}
                render={({
                  field: { ref, onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    placeholder="Password"
                    fullWidth
                    inputRef={ref}
                    type={showConfirmPassword ? "text" : "password"}
                    error={!!error}
                    helperText={error?.message}
                    onChange={onChange}
                    value={value}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label={
                                showConfirmPassword
                                  ? "hide the password"
                                  : "display the password"
                              }
                              onClick={() =>
                                handleClickShowPassword("CONFIRM_PASSWORD")
                              }
                              onMouseDown={handleMouseDownPassword}
                              onMouseUp={handleMouseUpPassword}
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Stack>
            <Stack
              direction={"row"}
              justifyContent={"end"}
              useFlexGap
              spacing={2}
            >
              <Button
                variant="outlined"
                onClick={() => props.setOpen(false)}
                sx={{ width: "120px" }}
              >
                Discard
              </Button>
              <Button
                type="submit"
                variant="contained"
                loading={isLoading}
                sx={{ width: "120px" }}
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      }
    />
  );
};

export default ChangePasswordForm;
